export type TokenStorageMode = 'memory' | 'memory-session-fallback';

export type TelemetrySink = 'console' | 'beacon' | 'none';

/**
 * Metadata for a known tenant, centralised here so tenant-facade.ts
 * does not need to maintain hardcoded maps.
 */
export interface TenantMetadata {
  /** Short display name, e.g. "Ross Robotics" */
  shortName: string;
  /** Formal / legal name, e.g. "Ross Robotics Ltd." */
  formalName: string;
  /** Email domain for tenant config lookup, e.g. "ross-robotics.co.uk" */
  domain: string;
}

export interface fleetManagementWidgetConfig {
  telemetryRefreshMs: number;
  membershipRefreshMs: number;
  defaultRobotsPerTenant: number;
  diagnosticsRetention: number;
  loadingDelayMs: number;
}

export interface MediaVisualiserConfig {
  networkSlowThresholdMs: number;
  maxPreviewDimension: number;
  wavesurferEnabled: boolean;
  sharedVideoPlaybackEnabled: boolean;
  audioChunkBytes: number;
  audioProgressiveExpectedBytes: number;
  audioMaxSourceBytes: number;
  audioFilterDebounceMs: number;
  audioSeekPreviewThrottleMs: number;
  audioProgressHeartbeatMs: number;
  audioAllowedOrigins: string[];
  /**
   * Cross-origin video hosts explicitly trusted for playback.
   * Empty means fail-closed to same-origin only.
   */
  videoAllowedOrigins: string[];
  /**
   * Cross-origin image hosts explicitly trusted for display.
   * Empty means fail-closed to same-origin only.
   */
  imageAllowedOrigins: string[];
}

export interface CoreRuntimeConfig {
  tokenStorageMode: TokenStorageMode;
  telemetrySink: TelemetrySink;
  /**
   * Email used to pre-load default tenant branding on the sign-in page.
   * Set to an empty string to skip pre-loading (neutral/white-label deployments).
   * Must be overridden per deployment via CoreRuntimeConfigOverride.
   */
  defaultSignInTenantEmail: string;
  /**
   * Centralised metadata for known tenants, keyed by tenant ID.
   * Used by TenantFacade for display name and domain lookups.
   */
  tenants: Record<string, TenantMetadata>;
  fleetManagementWidget: fleetManagementWidgetConfig;
  mediaVisualiser: MediaVisualiserConfig;
}

export type CoreRuntimeConfigOverride = Omit<
  Partial<CoreRuntimeConfig>,
  'fleetManagementWidget' | 'mediaVisualiser'
> & {
  fleetManagementWidget?: Partial<fleetManagementWidgetConfig>;
  mediaVisualiser?: Partial<MediaVisualiserConfig>;
};

export const DEFAULT_CORE_RUNTIME_CONFIG: CoreRuntimeConfig = {
  tokenStorageMode: 'memory',
  telemetrySink: 'console',
  defaultSignInTenantEmail: 'default@ross-robotics.co.uk',
  tenants: {
    // UUID keys mirror MOCK_CLIENT_ID_* in src/app/shared/testing/mock-entity-ids.constants.ts.
    // Core must not import from shared/testing, so these are duplicated inline.
    'a1b2c3d4-e5f6-7890-abcd-ef1234567890': {
      shortName: 'Ross Robotics',
      formalName: 'Ross Robotics Ltd.',
      domain: 'ross-robotics.co.uk',
    },
    'b2c3d4e5-f6a7-8901-bcde-f12345678901': {
      shortName: 'Robosynthesis',
      formalName: 'Robosynthesis Inc.',
      domain: 'robosynthesis.com',
    },
  },
  fleetManagementWidget: {
    telemetryRefreshMs: 2000,
    membershipRefreshMs: 30000,
    defaultRobotsPerTenant: 3,
    diagnosticsRetention: 20,
    loadingDelayMs: 200,
  },
  mediaVisualiser: {
    networkSlowThresholdMs: 700,
    maxPreviewDimension: 2048,
    wavesurferEnabled: true,
    sharedVideoPlaybackEnabled: true,
    audioChunkBytes: 262144,
    audioProgressiveExpectedBytes: 220200960,
    audioMaxSourceBytes: 220200960,
    audioFilterDebounceMs: 120,
    audioSeekPreviewThrottleMs: 75,
    audioProgressHeartbeatMs: 80,
    audioAllowedOrigins: [],
    videoAllowedOrigins: [],
    imageAllowedOrigins: [],
  },
};

export function resolveCoreRuntimeConfig(
  config?: CoreRuntimeConfigOverride
): CoreRuntimeConfig {
  const resolvedfleetManagementWidget = {
    ...DEFAULT_CORE_RUNTIME_CONFIG.fleetManagementWidget,
    ...(config?.fleetManagementWidget ?? {}),
  };

  const resolvedMediaVisualiser: MediaVisualiserConfig = {
    networkSlowThresholdMs:
      config?.mediaVisualiser?.networkSlowThresholdMs ??
      DEFAULT_CORE_RUNTIME_CONFIG.mediaVisualiser.networkSlowThresholdMs,
    maxPreviewDimension:
      config?.mediaVisualiser?.maxPreviewDimension ??
      DEFAULT_CORE_RUNTIME_CONFIG.mediaVisualiser.maxPreviewDimension,
    wavesurferEnabled:
      config?.mediaVisualiser?.wavesurferEnabled ??
      DEFAULT_CORE_RUNTIME_CONFIG.mediaVisualiser.wavesurferEnabled,
    sharedVideoPlaybackEnabled:
      config?.mediaVisualiser?.sharedVideoPlaybackEnabled ??
      DEFAULT_CORE_RUNTIME_CONFIG.mediaVisualiser.sharedVideoPlaybackEnabled,
    audioChunkBytes:
      config?.mediaVisualiser?.audioChunkBytes ??
      DEFAULT_CORE_RUNTIME_CONFIG.mediaVisualiser.audioChunkBytes,
    audioProgressiveExpectedBytes:
      config?.mediaVisualiser?.audioProgressiveExpectedBytes ??
      DEFAULT_CORE_RUNTIME_CONFIG.mediaVisualiser.audioProgressiveExpectedBytes,
    audioMaxSourceBytes:
      config?.mediaVisualiser?.audioMaxSourceBytes ??
      DEFAULT_CORE_RUNTIME_CONFIG.mediaVisualiser.audioMaxSourceBytes,
    audioFilterDebounceMs:
      config?.mediaVisualiser?.audioFilterDebounceMs ??
      DEFAULT_CORE_RUNTIME_CONFIG.mediaVisualiser.audioFilterDebounceMs,
    audioSeekPreviewThrottleMs:
      config?.mediaVisualiser?.audioSeekPreviewThrottleMs ??
      DEFAULT_CORE_RUNTIME_CONFIG.mediaVisualiser.audioSeekPreviewThrottleMs,
    audioProgressHeartbeatMs:
      config?.mediaVisualiser?.audioProgressHeartbeatMs ??
      DEFAULT_CORE_RUNTIME_CONFIG.mediaVisualiser.audioProgressHeartbeatMs,
    audioAllowedOrigins:
      config?.mediaVisualiser?.audioAllowedOrigins ??
      DEFAULT_CORE_RUNTIME_CONFIG.mediaVisualiser.audioAllowedOrigins,
    videoAllowedOrigins:
      config?.mediaVisualiser?.videoAllowedOrigins ??
      DEFAULT_CORE_RUNTIME_CONFIG.mediaVisualiser.videoAllowedOrigins,
    imageAllowedOrigins:
      config?.mediaVisualiser?.imageAllowedOrigins ??
      DEFAULT_CORE_RUNTIME_CONFIG.mediaVisualiser.imageAllowedOrigins,
  };

  return {
    ...DEFAULT_CORE_RUNTIME_CONFIG,
    ...(config ?? {}),
    tenants: {
      ...DEFAULT_CORE_RUNTIME_CONFIG.tenants,
      ...(config?.tenants ?? {}),
    },
    fleetManagementWidget: resolvedfleetManagementWidget,
    mediaVisualiser: resolvedMediaVisualiser,
  };
}
